import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { ApiError, api, historyApi, type CallRecord, type Directory, type HistoryCall, type HistoryCallsView, type HistorySearch, type HistorySort } from '../../api/client';
import { useTenant } from '../../components/Shell';
import { Button, Card, Chip, Drawer, Empty, Field, LoadError, Loading, Notice, PageHeader, SelectField } from '../../components/ui';
import { CallDetailView } from '../../components/CallDetail';
import { useData } from '../../lib/useData';
import { onChange } from '../../lib/changes';
import { fullWhen, plural, shownNumber } from '../../lib/format';
import { displayZone, timesIn, todayIn } from '../../lib/time';
import { Icon, type IconName } from '../../lib/icons';
import { ArchivedFiles, HistoryTabs } from './HistoryArchive';

/**
 * Call history (1.13.0, Teampace 3134; Joey, Nov 2: "standard search options… a filter that will
 * let us see calls with a transcript or not… the ability to sort records"): one list over every
 * date, searched on the server: Stitch's own records of each call, then the archive for older days.
 * The filters live in the address, so a search can be bookmarked or sent to someone.
 */

const PAGE_SIZE = 50;
const EXPORT_MAX = 5000;

export type When = 'today' | 'yesterday' | '7' | '30' | '90' | 'year' | 'custom';
export const WHEN: { value: When; label: string }[] = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
  { value: 'year', label: 'This year' },
  { value: 'custom', label: 'Choose dates' }
];

const SORTS: { value: HistorySort; label: string }[] = [
  { value: 'newest', label: 'Newest first' },
  { value: 'oldest', label: 'Oldest first' },
  { value: 'longest', label: 'Longest first' },
  { value: 'shortest', label: 'Shortest first' }
];

const LENGTHS = [
  { value: '', label: 'Any length' },
  { value: '30', label: 'Over 30 seconds' },
  { value: '60', label: 'Over 1 minute' },
  { value: '300', label: 'Over 5 minutes' },
  { value: '900', label: 'Over 15 minutes' }
];

const DIRECTION: Record<string, { label: string; icon: IconName }> = {
  inbound: { label: 'Incoming', icon: 'arrowIn' },
  outbound: { label: 'Outgoing', icon: 'arrowOut' },
  internal: { label: 'Internal', icon: 'arrowBoth' },
  unknown: { label: 'Call', icon: 'phone' }
};
const directionOf = (d: string) => DIRECTION[d] ?? DIRECTION.unknown!;

export function duration(seconds: number | undefined): string {
  if (seconds === undefined) return '';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return h ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}` : `${m}:${String(s).padStart(2, '0')}`;
}

const day = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const minus = (d: Date, days: number) => new Date(d.getFullYear(), d.getMonth(), d.getDate() - days, 12);

/** The days a choice covers, in the company's time (the display zone). */
export function rangeOf(when: When, custom: { from?: string; to?: string }, today = todayIn(displayZone())): { from: string; to: string } {
  switch (when) {
    case 'today':
      return { from: day(today), to: day(today) };
    case 'yesterday':
      return { from: day(minus(today, 1)), to: day(minus(today, 1)) };
    case '7':
      return { from: day(minus(today, 6)), to: day(today) };
    case '90':
      return { from: day(minus(today, 89)), to: day(today) };
    case 'year':
      return { from: `${today.getFullYear()}-01-01`, to: day(today) };
    case 'custom': {
      const from = custom.from && /^\d{4}-\d{2}-\d{2}$/.test(custom.from) ? custom.from : day(minus(today, 29));
      const to = custom.to && /^\d{4}-\d{2}-\d{2}$/.test(custom.to) ? custom.to : day(today);
      return from <= to ? { from, to } : { from: to, to: from };
    }
    default:
      return { from: day(minus(today, 29)), to: day(today) };
  }
}

const who = (p: { name: string; number: string }) => (p.name && p.number ? `${p.name} (${shownNumber(p.number)})` : p.name || shownNumber(p.number));
const lengthOf = (c: HistoryCall) => c.talkSeconds || c.seconds;

/** The search the address holds (anything it doesn't know is left out). */
function searchFrom(params: URLSearchParams): { when: When; search: Omit<HistorySearch, 'cursor'> } {
  const pick = <T extends string>(k: string, allowed: readonly T[]): T | undefined => {
    const v = params.get(k) as T | null;
    return v && allowed.includes(v) ? v : undefined;
  };
  const when = pick('when', ['today', 'yesterday', '7', '30', '90', 'year', 'custom'] as const) ?? '30';
  const { from, to } = rangeOf(when, { from: params.get('from') ?? undefined, to: params.get('to') ?? undefined });
  const longer = Number(params.get('longer'));
  const q = (params.get('q') ?? '').trim().slice(0, 80);
  const ext = (params.get('ext') ?? '').replace(/[^0-9*#]/g, '').slice(0, 12);
  const direction = pick('direction', ['inbound', 'outbound', 'internal'] as const);
  const result = pick('result', ['answered', 'missed', 'voicemail'] as const);
  const recorded = pick('recorded', ['yes', 'no'] as const);
  const transcript = pick('transcript', ['yes', 'no'] as const);
  return {
    when,
    search: {
      from,
      to,
      sort: pick('sort', ['newest', 'oldest', 'longest', 'shortest'] as const) ?? 'newest',
      ...(q ? { q } : {}),
      ...(ext ? { ext } : {}),
      ...(direction ? { direction } : {}),
      ...(result ? { result } : {}),
      ...(recorded ? { recorded } : {}),
      ...(transcript ? { transcript } : {}),
      ...(Number.isInteger(longer) && longer > 0 && longer <= 86_400 ? { longer } : {})
    }
  };
}

function toCsv(calls: HistoryCall[]): string {
  const cell = (v: string | number | null | undefined) => {
    const s = v === undefined || v === null ? '' : String(v);
    // Quote everything; neutralise spreadsheet formulas.
    return `"${(/^[=+\-@]/.test(s) ? `'${s}` : s).replace(/"/g, '""')}"`;
  };
  const head = ['Start', 'Answered', 'End', 'Direction', 'From name', 'From number', 'To name', 'To number', 'Result', 'Length (s)', 'Talk (s)', 'Recording', 'Transcript', 'Extensions', 'Answered by', 'Kept in'];
  const kept = { stitch: 'Stitch', archive: 'Stitch archive', 'your-storage': 'Your storage' };
  const rows = calls.map((c) =>
    [
      c.startedAt,
      c.answeredAt,
      c.endedAt,
      c.patch ? c.patch.text : directionOf(c.direction).label,
      c.from.name,
      c.from.number,
      c.to.name,
      c.to.number,
      c.result,
      c.seconds,
      c.talkSeconds,
      c.hasRecording ? 'yes' : 'no',
      c.hasTranscript ? 'yes' : 'no',
      c.extensions.join(' '),
      c.answeredBy,
      kept[c.from_]
    ]
      .map(cell)
      .join(',')
  );
  return [head.map(cell).join(','), ...rows].join('\r\n');
}

/** "Person or line": people, then queues, ring groups and auto attendants, from the company's directory. */
export function lineOptions(dir: Directory | undefined, ext: string | undefined): { value: string; label: string; group?: string }[] {
  const groups: Record<string, string> = { person: 'People', callQueue: 'Call queues', ringGroup: 'Ring groups', autoAttendant: 'Auto attendants' };
  const out: { value: string; label: string; group?: string }[] = [{ value: '', label: 'Anyone' }];
  for (const kind of Object.keys(groups)) {
    for (const e of (dir?.entries ?? []).filter((x) => x.kind === kind)) out.push({ value: e.extension, label: e.name ? `${e.name} (${e.extension})` : e.extension, group: groups[kind]! });
  }
  // An extension from the address that isn't in the directory still shows.
  if (ext && !out.some((o) => o.value === ext)) out.push({ value: ext, label: `Extension ${ext}` });
  return out;
}

export function TenantCalls() {
  const { tenant } = useTenant();
  const [params, setParams] = useSearchParams();
  const { when, search } = useMemo(() => searchFrom(params), [params]);
  const key = JSON.stringify(search);
  const [calls, setCalls] = useState<HistoryCall[]>([]);
  const [last, setLast] = useState<HistoryCallsView | null>(null);
  const [pages, setPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error>();
  const [tick, setTick] = useState(0);
  const [open, setOpen] = useState<HistoryCall | null>(null);
  const [exporting, setExporting] = useState('');
  const [showPhoneSystem, setShowPhoneSystem] = useState(false);
  const [text, setText] = useState(search.q ?? '');
  const seq = useRef(0);
  // Archive only (2717): everything comes from what Stitch kept; the phone system isn't asked.
  const archiveOnly = tenant.customer?.status === 'archive_only';
  const dir = useData(() => (archiveOnly ? Promise.resolve(undefined) : api.directory(tenant.domain).catch(() => undefined)), [tenant.domain, archiveOnly]);

  // The filters as last set here. React Router hands a functional update the address from the last
  // render, so a filter chosen right after Clear filters (before the page draws again) brought the
  // cleared ones back. Each change builds on the one before it instead.
  const latest = useRef(params);
  useEffect(() => {
    latest.current = params;
  }, [params]);
  const replaceParams = (next: URLSearchParams) => {
    latest.current = next;
    setParams(next, { replace: true });
  };
  const set = (changes: Record<string, string | undefined>) => {
    const next = new URLSearchParams(latest.current);
    for (const [k, v] of Object.entries(changes)) {
      if (v === undefined || v === '') next.delete(k);
      else next.set(k, v);
    }
    replaceParams(next);
  };

  // The search box: the list follows a moment after typing stops.
  useEffect(() => setText(search.q ?? ''), [search.q]);
  useEffect(() => {
    if (text.trim() === (search.q ?? '')) return;
    const t = setTimeout(() => set({ q: text.trim() || undefined }), 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);

  // The first page, whenever the search changes (or a call ends while the first page is showing).
  useEffect(() => {
    const mine = ++seq.current;
    setLoading(true);
    historyApi
      .calls(tenant.domain, { ...search, limit: PAGE_SIZE })
      .then((r) => {
        if (mine !== seq.current) return;
        setCalls(r.calls);
        setLast(r);
        setPages(1);
        setError(undefined);
      })
      .catch((e: unknown) => mine === seq.current && setError(e instanceof Error ? e : new Error(String(e))))
      .finally(() => mine === seq.current && setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant.domain, key, tick]);
  useEffect(
    () =>
      onChange((n) => {
        if (n.topic === 'calls' && (!n.tenant || n.tenant === tenant.domain.toLowerCase()) && pages <= 1) setTick((t) => t + 1);
      }),
    [tenant.domain, pages]
  );

  const more = async () => {
    if (!last?.next) return;
    const mine = ++seq.current;
    setLoading(true);
    try {
      const r = await historyApi.calls(tenant.domain, { ...search, cursor: last.next, limit: PAGE_SIZE });
      if (mine !== seq.current) return;
      setCalls((cur) => {
        const seen = new Set(cur.map((c) => `${c.from_}-${c.id}`));
        return [...cur, ...r.calls.filter((c) => !seen.has(`${c.from_}-${c.id}`))];
      });
      setLast(r);
      setPages((p) => p + 1);
    } catch (e) {
      if (mine === seq.current) setError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      if (mine === seq.current) setLoading(false);
    }
  };

  /** The whole search as CSV (up to 5,000 calls), a page after another. */
  const exportCsv = async () => {
    setExporting('Exporting…');
    try {
      const all: HistoryCall[] = [];
      let cursor: string | undefined;
      do {
        const r = await historyApi.calls(tenant.domain, { ...search, limit: 100, ...(cursor ? { cursor } : {}) });
        all.push(...r.calls);
        cursor = r.next ?? undefined;
        setExporting(`Exporting… ${all.length}`);
      } while (cursor && all.length < EXPORT_MAX);
      const blob = new Blob([toCsv(all.slice(0, EXPORT_MAX))], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `calls-${tenant.domain}-${search.from}-to-${search.to}.csv`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      setExporting('');
    } catch (e) {
      setExporting('');
      setError(e instanceof Error ? e : new Error(String(e)));
    }
  };

  const filtered = Boolean(search.q || search.ext || search.direction || search.result || search.recorded || search.transcript || search.longer || when !== '30' || search.sort !== 'newest');
  const notAvailable = error instanceof ApiError && (error.code === 'NOT_AVAILABLE' || error.code === 'NOT_YET' || error.status === 403);
  const sortBy = (by: 'time' | 'length') => {
    const s = search.sort ?? 'newest';
    const next: HistorySort = by === 'time' ? (s === 'newest' ? 'oldest' : 'newest') : s === 'longest' ? 'shortest' : 'longest';
    set({ sort: next === 'newest' ? undefined : next });
  };
  const sortHeader = (by: 'time' | 'length', label: string) => {
    const s = search.sort ?? 'newest';
    const active = by === 'time' ? s === 'newest' || s === 'oldest' : s === 'longest' || s === 'shortest';
    const desc = s === 'newest' || s === 'longest';
    return (
      <th scope="col" aria-sort={active ? (desc ? 'descending' : 'ascending') : 'none'}>
        <button type="button" className="sort-btn" onClick={() => sortBy(by)}>
          {label}
          {active && <Icon name="chev" size={13} strokeWidth={2.4} />}
        </button>
      </th>
    );
  };
  // Stitch kept calls from this day: a search that starts earlier may be missing the ones before.
  const keptSince = last?.keptSince ? last.keptSince.slice(0, 10) : null;
  const reachesBefore = !loading && !last?.next && keptSince !== null && search.from < keptSince && (last?.hotFrom === null || last?.hotFrom === undefined);

  // Stitch hasn't kept a single call of this company (its call records service isn't receiving
  // them): the phone system's own list, as before 1.13.0, rather than an empty search.
  if (last && last.keptSince === null && last.hotFrom === null) {
    return (
      <>
        <PageHeader eyebrow={timesIn()} title="Call history" />
        <div className="content">
          <HistoryTabs />
          <Notice>Search, filters and sorting work on the calls Stitch keeps, and Stitch hasn’t received any of your calls yet. Here’s the phone system’s own list.</Notice>
          <PhoneSystemList domain={tenant.domain} dir={dir.data} full />
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader
        eyebrow={`${calls.length ? `${plural(calls.length, 'call')}${last?.next ? '+' : ''} · ` : ''}${timesIn()}`}
        title="Call history"
        actions={
          <>
            <label className="search">
              <Icon name="search" size={17} strokeWidth={2} />
              <span className="sr-only">Search calls</span>
              <input type="search" placeholder="Search name, number or extension" value={text} onChange={(e) => setText(e.target.value)} />
            </label>
            <Button icon="download" disabled={calls.length === 0 || Boolean(exporting)} onClick={() => void exportCsv()}>
              {exporting || 'Export'}
            </Button>
          </>
        }
      />
      <div className="content">
        <HistoryTabs />
        <div className="call-filters" role="group" aria-label="Filter and sort calls">
          <SelectField label="When" value={when} onChange={(e) => set({ when: e.target.value === '30' ? undefined : e.target.value, ...(e.target.value === 'custom' ? { from: search.from, to: search.to } : { from: undefined, to: undefined }) })} options={WHEN} />
          {when === 'custom' && (
            <>
              <Field label="From" type="date" value={search.from} max={search.to} onChange={(e) => set({ from: e.target.value || undefined })} />
              <Field label="To" type="date" value={search.to} min={search.from} onChange={(e) => set({ to: e.target.value || undefined })} />
            </>
          )}
          <SelectField
            label="Direction"
            value={search.direction ?? ''}
            onChange={(e) => set({ direction: e.target.value })}
            options={[
              { value: '', label: 'Any direction' },
              { value: 'inbound', label: 'Incoming' },
              { value: 'outbound', label: 'Outgoing' },
              { value: 'internal', label: 'Internal' }
            ]}
          />
          <SelectField
            label="Result"
            value={search.result ?? ''}
            onChange={(e) => set({ result: e.target.value })}
            options={[
              { value: '', label: 'Any result' },
              { value: 'answered', label: 'Answered' },
              { value: 'missed', label: 'Missed' },
              { value: 'voicemail', label: 'Voicemail' }
            ]}
          />
          <SelectField label="Person or line" value={search.ext ?? ''} onChange={(e) => set({ ext: e.target.value })} options={lineOptions(dir.data, search.ext)} />
          <SelectField label="Length" value={search.longer ? String(search.longer) : ''} onChange={(e) => set({ longer: e.target.value })} options={LENGTHS} />
          <SelectField
            label="Recording"
            value={search.recorded ?? ''}
            onChange={(e) => set({ recorded: e.target.value })}
            options={[
              { value: '', label: 'Any' },
              { value: 'yes', label: 'Recorded' },
              { value: 'no', label: 'Not recorded' }
            ]}
          />
          <SelectField
            label="Transcript"
            value={search.transcript ?? ''}
            onChange={(e) => set({ transcript: e.target.value })}
            options={[
              { value: '', label: 'Any' },
              { value: 'yes', label: 'Has a transcript' },
              { value: 'no', label: 'No transcript' }
            ]}
          />
          <SelectField label="Sort" value={search.sort ?? 'newest'} onChange={(e) => set({ sort: e.target.value === 'newest' ? undefined : e.target.value })} options={SORTS} />
          {filtered && (
            <div className="call-filters-clear">
              <Button
                variant="ghost"
                onClick={() => {
                  setText('');
                  replaceParams(new URLSearchParams());
                }}
              >
                Clear filters
              </Button>
            </div>
          )}
        </div>
        {last?.scope === 'queues' && <Notice>Showing calls that went to the call queues your role covers, and calls on your own extension.</Notice>}
        {last?.scope === 'own' && <Notice>Showing the calls on your own extension.</Notice>}
        {last?.sortedFrom && (
          <Notice>
            Sorted by length from {fullDay(last.sortedFrom)}, the calls Stitch keeps itself. Choose Newest or Oldest first to include the dates before.
          </Notice>
        )}
        {last?.archive === 'unavailable' && <Notice tone="warn">Older calls are in Stitch’s archive, which isn’t answering right now. Try again in a few minutes.</Notice>}
        {last?.archive === 'your-storage-unreachable' && <Notice tone="warn">Some older calls are only in your own storage, which isn’t answering. Settings → Storage and retention shows what it said.</Notice>}
        <Card>
          {notAvailable ? (
            <div className="card-body">
              <Notice title="Not available">{error.message}</Notice>
            </div>
          ) : error && calls.length === 0 ? (
            <LoadError error={error} retry={() => setTick((t) => t + 1)} />
          ) : loading && calls.length === 0 ? (
            <Loading label="Loading calls" />
          ) : calls.length === 0 ? (
            <Empty title={filtered ? 'No calls match' : 'No calls yet'}>{filtered ? 'Try other dates, or fewer filters.' : 'Calls show here once your phone system records them.'}</Empty>
          ) : (
            <div className="table-wrap">
              <table className="table calls">
                <thead>
                  <tr>
                    {sortHeader('time', 'When')}
                    <th scope="col">From</th>
                    <th scope="col">To</th>
                    {sortHeader('length', 'Length')}
                    <th scope="col" className="hide-sm">
                      Went through
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {calls.map((c) => {
                    const d = directionOf(c.direction);
                    return (
                      <tr key={`${c.from_}-${c.id}`}>
                        <td>
                          <span className="call-when">
                            <span className={`call-dir ${c.result === 'missed' ? 'missed' : c.direction}`} title={c.result === 'missed' ? 'Missed' : d.label}>
                              <Icon name={d.icon} size={15} strokeWidth={2.2} label={c.result === 'missed' ? 'Missed' : d.label} />
                            </span>
                            <button type="button" className="link-btn" onClick={() => setOpen(c)} aria-label={`Open the call at ${fullWhen(c.startedAt)}`}>
                              {fullWhen(c.startedAt)}
                            </button>
                            {c.from_ === 'your-storage' && (
                              <Chip small tone="brand">
                                From your storage
                              </Chip>
                            )}
                          </span>
                        </td>
                        {c.patch ? (
                          <td colSpan={2}>
                            <Icon name="headset" size={14} /> {c.patch.text}
                          </td>
                        ) : (
                          <>
                            <td>{who(c.from) || <span className="muted">Unknown</span>}</td>
                            <td>{who(c.to) || <span className="muted">Unknown</span>}</td>
                          </>
                        )}
                        <td className="mono">
                          <span className="call-length">
                            {c.result === 'missed' ? (
                              <Chip small tone="crit">
                                Missed
                              </Chip>
                            ) : c.result === 'voicemail' ? (
                              <Chip small>Voicemail</Chip>
                            ) : (
                              duration(lengthOf(c))
                            )}
                            {c.hasRecording && <Icon name="mic" size={14} label="Recorded" />}
                            {c.hasTranscript && <Icon name="file" size={14} label="Has a transcript" />}
                          </span>
                        </td>
                        <td className="hide-sm mono">{c.extensions.length ? c.extensions.join(', ') : <span className="muted">–</span>}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {calls.length > 0 && last?.next && !notAvailable && (
            <div className="card-foot">
              <Button variant="ghost" disabled={loading} onClick={() => void more()}>
                {loading ? 'Loading…' : 'Load more'}
              </Button>
            </div>
          )}
        </Card>
        {reachesBefore && !showPhoneSystem && !archiveOnly && (
          <Notice>
            Stitch has kept your calls since {fullDay(keptSince)}. Calls from before that are in the phone system’s own list.{' '}
            <button type="button" className="link-btn" onClick={() => setShowPhoneSystem(true)}>
              Show the phone system’s list
            </button>
          </Notice>
        )}
        {showPhoneSystem && <PhoneSystemList domain={tenant.domain} dir={dir.data} />}
      </div>
      <Drawer open={open !== null} onClose={() => setOpen(null)} title={open ? (open.patch ? open.patch.text : `${who(open.from) || 'Unknown'} → ${who(open.to) || 'Unknown'}`) : 'Call'} subtitle="Where it went, who it rang, how it sounded.">
        {open && <OpenCall domain={tenant.domain} call={open} dir={dir.data} archiveOnly={archiveOnly} />}
      </Drawer>
    </>
  );
}

/** "Tuesday, November 3, 2026" from 2026-11-03. */
export function fullDay(d: string): string {
  const [y, m, dd] = d.split('-').map(Number) as [number, number, number];
  return new Date(y, m - 1, dd, 12).toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
}

/** A call opened: the phone system's full record when it still has it, else what Stitch kept. */
function OpenCall({ domain, call, dir, archiveOnly = false }: { domain: string; call: HistoryCall; dir: Directory | undefined; archiveOnly?: boolean }) {
  const record = useData(() => (call.from_ === 'stitch' && !archiveOnly ? historyApi.record(domain, call.id).then((r) => r.recordId) : Promise.resolve(null)), [domain, call.id, call.from_, archiveOnly]);
  if (record.loading && record.data === undefined) return <Loading label="Finding the call" />;
  if (record.data) return <CallDetailView domain={domain} record={record.data} dir={dir} />;
  return (
    <div className="stack">
      {archiveOnly ? (
        <Notice>What Stitch kept of this call (your company is archive only).</Notice>
      ) : call.from_ !== 'stitch' ? (
        <Notice>{call.from_ === 'your-storage' ? 'From your own storage: ' : 'From Stitch’s archive: '}what Stitch kept of this call. The phone system no longer has its full record.</Notice>
      ) : (
        record.error === undefined && <Notice>What Stitch kept of this call. The phone system’s full record of it wasn’t found.</Notice>
      )}
      <dl className="dl">
        <dt>When</dt>
        <dd>{fullWhen(call.startedAt)}</dd>
        <dt>Direction</dt>
        <dd>{call.patch ? call.patch.text : directionOf(call.direction).label}</dd>
        <dt>From</dt>
        <dd>{who(call.from) || 'Unknown'}</dd>
        <dt>To</dt>
        <dd>{who(call.to) || 'Unknown'}</dd>
        <dt>Result</dt>
        <dd>{call.result === 'answered' ? `Answered${call.answeredBy ? ` at ${call.answeredBy}` : ''}` : call.result === 'voicemail' ? `Voicemail${call.mailbox ? ` (${call.mailbox})` : ''}` : 'Missed'}</dd>
        <dt>Length</dt>
        <dd>{call.talkSeconds ? `${duration(call.talkSeconds)} talking, ${duration(call.seconds)} in all` : duration(call.seconds)}</dd>
        <dt>Went through</dt>
        <dd>{call.extensions.length ? call.extensions.join(', ') : '–'}</dd>
        <dt>Recording</dt>
        <dd>{call.hasRecording ? (call.hasTranscript ? 'Recorded, with a transcript' : 'Recorded') : 'Not recorded'}</dd>
      </dl>
      {/* An archived call's recording, played through Stitch (2716). */}
      {(call.from_ !== 'stitch' || archiveOnly) && call.hasRecording && <ArchivedFiles domain={domain} table="calls" id={call.id} at={call.startedAt} />}
    </div>
  );
}

/**
 * The phone system's own list of calls (newest pages first), filtered in the browser, the way Call
 * history worked before 1.13.0: for calls from before Stitch kept them, and for a company whose
 * calls Stitch hasn't received yet (`full`: with its quick filters and export).
 */
type Quick = 'all' | 'inbound' | 'outbound' | 'internal' | 'missed';
const QUICK: { value: Quick; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'inbound', label: 'Incoming' },
  { value: 'outbound', label: 'Outgoing' },
  { value: 'internal', label: 'Internal' },
  { value: 'missed', label: 'Missed' }
];

function phoneListCsv(calls: CallRecord[]): string {
  const cell = (v: string | number | undefined) => {
    const s = v === undefined ? '' : String(v);
    return `"${(/^[=+\-@]/.test(s) ? `'${s}` : s).replace(/"/g, '""')}"`;
  };
  const head = ['Start', 'Answered', 'End', 'Direction', 'From name', 'From number', 'To name', 'To number', 'Duration (s)', 'Missed', 'Via'];
  const rows = calls.map((c) =>
    [c.start, c.answered, c.end, c.patch ? c.patch.text : directionOf(c.direction).label, c.from.name, c.from.number, c.to.name, c.to.number, c.durationSeconds, c.missed ? 'yes' : 'no', c.via.join(' ')]
      .map(cell)
      .join(',')
  );
  return [head.map(cell).join(','), ...rows].join('\r\n');
}

function PhoneSystemList({ domain, dir, full }: { domain: string; dir: Directory | undefined; full?: boolean }) {
  const [calls, setCalls] = useState<CallRecord[]>([]);
  const [page, setPage] = useState(1);
  const [more, setMore] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error>();
  const [scoped, setScoped] = useState(false);
  const [quick, setQuick] = useState<Quick>('all');
  const [q, setQ] = useState('');
  const [tick, setTick] = useState(0);
  const [openCall, setOpenCall] = useState<CallRecord | null>(null);
  useEffect(() => {
    let live = true;
    setLoading(true);
    api
      .calls(domain, page, PAGE_SIZE)
      .then((r) => {
        if (!live) return;
        setCalls((cur) => {
          const seen = new Set(cur.map((c) => c.id));
          return page === 1 ? r.calls : [...cur, ...r.calls.filter((c) => !seen.has(c.id))];
        });
        setMore(r.more ?? r.calls.length === PAGE_SIZE);
        setScoped(Boolean(r.scoped));
        setError(undefined);
      })
      .catch((e: unknown) => live && setError(e instanceof Error ? e : new Error(String(e))))
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [domain, page, tick]);
  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return calls.filter((c) => {
      if (quick === 'missed' ? !c.missed : quick !== 'all' && c.direction !== quick) return false;
      if (!needle) return true;
      return [c.from.name, c.from.number, c.to.name, c.to.number, ...c.via].some((v) => v.toLowerCase().includes(needle));
    });
  }, [calls, quick, q]);
  const notAvailable = error instanceof ApiError && (error.code === 'NOT_AVAILABLE' || error.code === 'NOT_YET');
  const exportCsv = () => {
    const blob = new Blob([phoneListCsv(rows)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `calls-${domain}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  const one = (p: CallRecord['from']) => who(p);
  return (
    <>
      {scoped && <Notice>Showing calls that went to the call queues your role covers.</Notice>}
      <div className="phone-list-tools">
        <div className="filters" role="group" aria-label="Show">
          {QUICK.map((f) => (
            <button key={f.value} type="button" aria-pressed={quick === f.value} onClick={() => setQuick(f.value)}>
              {f.label}
            </button>
          ))}
        </div>
        <label className="search">
          <Icon name="search" size={17} strokeWidth={2} />
          <span className="sr-only">Search the phone system’s list</span>
          <input type="search" placeholder="Search name, number or extension" value={q} onChange={(e) => setQ(e.target.value)} />
        </label>
        {full && (
          <Button icon="download" disabled={rows.length === 0} onClick={exportCsv}>
            Export
          </Button>
        )}
      </div>
      <Card title={full ? undefined : 'The phone system’s list'}>
        {notAvailable ? (
          <div className="card-body">
            <Notice title="Not available">{error.message}</Notice>
          </div>
        ) : error && calls.length === 0 ? (
          <LoadError error={error} retry={() => setTick((t) => t + 1)} />
        ) : loading && calls.length === 0 ? (
          <Loading label="Loading calls" />
        ) : rows.length === 0 ? (
          <Empty title={calls.length ? 'No calls match' : 'No calls yet'}>{calls.length ? 'Try another filter or search.' : 'Calls show here once your phone system records them.'}</Empty>
        ) : (
          <div className="table-wrap">
            <table className="table calls">
              <thead>
                <tr>
                  <th scope="col">When</th>
                  <th scope="col">From</th>
                  <th scope="col">To</th>
                  <th scope="col">Length</th>
                  <th scope="col" className="hide-sm">
                    Went through
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => (
                  <tr key={c.id}>
                    <td>
                      <span className="call-when">
                        <span className={`call-dir ${c.missed ? 'missed' : c.direction}`} title={c.missed ? 'Missed' : directionOf(c.direction).label}>
                          <Icon name={directionOf(c.direction).icon} size={15} strokeWidth={2.2} label={c.missed ? 'Missed' : directionOf(c.direction).label} />
                        </span>
                        {c.recordId ? (
                          <button type="button" className="link-btn" onClick={() => setOpenCall(c)} aria-label={`Open the call at ${c.start ? fullWhen(c.start) : 'unknown time'}`}>
                            {c.start ? fullWhen(c.start) : '–'}
                          </button>
                        ) : (
                          <span>{c.start ? fullWhen(c.start) : '–'}</span>
                        )}
                      </span>
                    </td>
                    {c.patch ? (
                      <td colSpan={2}>
                        <Icon name="headset" size={14} /> {c.patch.text}
                      </td>
                    ) : (
                      <>
                        <td>{one(c.from) || <span className="muted">Unknown</span>}</td>
                        <td>{one(c.to) || <span className="muted">Unknown</span>}</td>
                      </>
                    )}
                    <td className="mono">{c.missed ? <Chip small tone="crit">Missed</Chip> : duration(c.durationSeconds) || <span className="muted">–</span>}</td>
                    <td className="hide-sm mono">{c.via.length ? c.via.join(' → ') : <span className="muted">–</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {calls.length > 0 && more && !notAvailable && (
          <div className="card-foot">
            <Button variant="ghost" disabled={loading} onClick={() => setPage((p) => p + 1)}>
              {loading ? 'Loading…' : 'Load more'}
            </Button>
          </div>
        )}
      </Card>
      <Drawer open={openCall !== null} onClose={() => setOpenCall(null)} title={openCall ? `${one(openCall.from) || 'Unknown'} → ${one(openCall.to) || 'Unknown'}` : 'Call'} subtitle="Where it went, who it rang, how it sounded.">
        {openCall?.recordId && <CallDetailView domain={domain} record={openCall.recordId} dir={dir} />}
      </Drawer>
    </>
  );
}
